import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Footer from '../components/Footer';
import ArrowIcon from '../components/ArrowIcon';
import BlogCoverArt from '../components/BlogCoverArt';
import { subscribePublishedPosts } from '../data/blogService';

const PAGE_SIZE = 6;

function formatDate(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/* ── Auto-rotating "trending" hero ── */
function FeaturedCarousel({ posts }) {
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (posts.length < 2) return;
    const t = setInterval(() => setActive((i) => (i + 1) % posts.length), 5200);
    return () => clearInterval(t);
  }, [posts.length]);

  if (!posts.length) return null;
  const post = posts[active];

  return (
    <div
      className="blog-hero-carousel"
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0,1.05fr) minmax(0,1fr)',
        borderRadius: 22,
        overflow: 'hidden',
        border: '1px solid var(--line)',
        boxShadow: '0 20px 50px rgba(10,10,20,0.10)',
        background: 'white',
        flex: '0 0 auto',
        height: 'clamp(180px, 26vh, 360px)',
        minHeight: 0,
      }}
    >
      <BlogCoverArt image={post.image} alt={post.title} rgb={post.rgb} badge="Trending" meta={[post.readTime, formatDate(post.date)]} />

      <div
        style={{
          padding: 'clamp(12px, 2.4vh, 40px) clamp(20px, 3.2vw, 48px)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          minWidth: 0,
          overflow: 'hidden',
          gap: 'clamp(8px, 1.6vh, 14px)',
        }}
      >
        {/* Progress dots + category badge, sharing one row to save vertical space */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexShrink: 0 }}>
          <div
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 7, width: 'fit-content',
              padding: '5px 13px', borderRadius: 999,
              border: `1.4px solid ${post.accent}`, color: post.accent,
              fontSize: 10.5, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase',
            }}
          >
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: post.accent }} />
            {post.categoryLabel}
          </div>
          <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
            {posts.map((_, i) => (
              <span
                key={i}
                style={{
                  width: i === active ? 20 : 6,
                  height: 6,
                  borderRadius: 4,
                  background: i === active ? post.accent : 'rgba(10,10,20,0.15)',
                  transition: 'all 300ms ease',
                }}
              />
            ))}
          </div>
        </div>

        <h2
          style={{
            fontFamily: "'Anton', sans-serif",
            fontWeight: 400,
            fontSize: 'clamp(17px, 2.6vh, 30px)',
            lineHeight: 1.16,
            color: 'var(--ink)',
            flexShrink: 0,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {post.title}
        </h2>

        <p
          style={{
            fontSize: 'clamp(12px, 1.5vh, 14.5px)',
            lineHeight: 1.5,
            color: 'var(--ink-soft)',
            flexShrink: 0,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {post.excerpt}
        </p>

        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', flexShrink: 0 }}>
          <Link
            to={`/blog/${post.slug}`}
            className="hoverable"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 9, width: 'fit-content',
              padding: 'clamp(8px, 1.2vh, 11px) 22px', background: 'var(--ink)', color: 'white',
              borderRadius: 999, fontSize: 12.5, fontWeight: 700,
            }}
          >
            Read Article <ArrowIcon size={12} />
          </Link>
          <span style={{ fontSize: 12, color: 'var(--ink-soft)', fontWeight: 600 }}>
            By {post.author}
          </span>
        </div>
      </div>
    </div>
  );
}

/* ── Grid card ── */
function BlogCard({ post }) {
  return (
    <Link
      to={`/blog/${post.slug}`}
      className="hoverable blog-card"
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        borderRadius: 18,
        overflow: 'hidden',
        background: 'white',
        border: '1px solid var(--line)',
        boxShadow: '0 6px 20px rgba(10,10,20,0.05)',
        transition: 'transform 220ms ease, box-shadow 220ms ease',
      }}
    >
      <BlogCoverArt
        image={post.image}
        alt={post.title}
        rgb={post.rgb}
        badge={post.categoryLabel}
        meta={[post.readTime, formatDate(post.date)]}
        height="clamp(180px, 15vw, 220px)"
        compact
      />
      <div style={{ padding: '18px 20px 20px', display: 'flex', flexDirection: 'column', flex: 1, gap: 8 }}>
        <h3
          style={{
            fontFamily: "'Anton', sans-serif",
            fontWeight: 400,
            fontSize: 'clamp(16px, 1.4vw, 19px)',
            lineHeight: 1.22,
            color: 'var(--ink)',
            minHeight: '2.44em',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {post.title}
        </h3>
        <p
          style={{
            fontSize: 13,
            lineHeight: 1.5,
            color: 'var(--ink-soft)',
            flex: '1 1 auto',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {post.excerpt}
        </p>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 11.5, color: 'var(--ink-soft)', fontWeight: 600, flexShrink: 0 }}>
          <span>{post.author}</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: post.accent, fontWeight: 700 }}>
            Read <ArrowIcon size={9} />
          </span>
        </div>
      </div>
    </Link>
  );
}

export default function BlogPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const gridRef = useRef(null);

  useEffect(() => {
    const unsubscribe = subscribePublishedPosts((next) => {
      setPosts(next);
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  const featuredPosts = useMemo(() => posts.filter((p) => p.featured), [posts]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return posts;
    return posts.filter(
      (p) =>
        p.title.toLowerCase().includes(q) ||
        p.excerpt.toLowerCase().includes(q) ||
        p.categoryLabel.toLowerCase().includes(q)
    );
  }, [posts, query]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const searching = query.trim().length > 0;

  const goToPage = (n) => {
    setPage(n);
    gridRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    name: 'Zone-X Blog',
    description: 'Engineering insights on IoT product design, embedded systems, firmware, cloud dashboards, and edge AI from the Zone-X Laboratories team.',
    url: 'https://zone-x.dev/blog',
    blogPost: posts.map((p) => ({
      '@type': 'BlogPosting',
      headline: p.title,
      description: p.excerpt,
      datePublished: p.date,
      author: { '@type': 'Person', name: p.author },
      url: `https://zone-x.dev/blog/${p.slug}`,
    })),
  };

  return (
    <div className="blog-page-shell">
      <Helmet>
        <title>Zone-X Blog — IoT, Embedded Systems &amp; Engineering Insights</title>
        <meta
          name="description"
          content="Practical engineering insights on IoT product design, embedded systems, firmware, cloud dashboards, and edge AI — from the team shipping connected products at Zone-X Laboratories."
        />
        <meta property="og:title" content="Zone-X Blog — IoT, Embedded Systems & Engineering Insights" />
        <meta property="og:description" content="Practical engineering insights on IoT, embedded systems, firmware, cloud dashboards, and edge AI from Zone-X Laboratories." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://zone-x.dev/blog" />
        <link rel="canonical" href="https://zone-x.dev/blog" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <div
        className="blog-page-inner"
        style={{
          display: 'flex',
          flexDirection: 'column',
          padding: 'clamp(88px, 11vh, 120px) clamp(24px, 4vw, 64px) clamp(48px, 7vh, 88px)',
          maxWidth: 1480,
          margin: '0 auto',
          width: '100%',
          gap: 'clamp(12px, 2vh, 30px)',
        }}
      >
        {/* Header row */}
        <div
          className="blog-header-row"
          style={{
            flex: '0 0 auto',
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            gap: 20,
            flexWrap: 'wrap',
          }}
        >
          <div>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <span style={{ fontFamily: "'Anton', sans-serif", fontSize: 11, color: 'var(--red)' }}>/</span>
              <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ink-soft)' }}>
                Insights &amp; Engineering Notes
              </span>
            </div>
            <h1
              style={{
                fontFamily: "'Anton', sans-serif",
                fontWeight: 400,
                fontSize: 'clamp(28px, 4vw, 44px)',
                lineHeight: 1,
                color: 'var(--ink)',
              }}
            >
              ZONE-X{' '}
              <span style={{ fontFamily: "'Instrument Serif', serif", fontStyle: 'italic', fontWeight: 400, color: 'var(--red)' }}>
                blog
              </span>
            </h1>
          </div>

          <div style={{ position: 'relative', width: 'min(320px, 100%)' }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', opacity: 0.45 }}>
              <circle cx="11" cy="11" r="7" stroke="var(--ink)" strokeWidth="2" />
              <path d="M21 21l-4.35-4.35" stroke="var(--ink)" strokeWidth="2" strokeLinecap="round" />
            </svg>
            <input
              value={query}
              onChange={(e) => { setQuery(e.target.value); setPage(1); }}
              placeholder="Search articles…"
              aria-label="Search blog articles"
              style={{
                width: '100%',
                padding: '11px 16px 11px 40px',
                borderRadius: 999,
                border: '1px solid var(--line)',
                background: 'rgba(255,255,255,0.8)',
                fontSize: 13,
                fontFamily: 'inherit',
                color: 'var(--ink)',
                outline: 'none',
              }}
            />
          </div>
        </div>

        {/* Featured / trending carousel */}
        {!searching && !loading && posts.length > 0 && (
          <FeaturedCarousel posts={featuredPosts.length ? featuredPosts : posts.slice(0, 3)} />
        )}

        {/* Grid section */}
        <div className="blog-grid-section" ref={gridRef} style={{ display: 'flex', flexDirection: 'column', gap: 14, scrollMarginTop: 96 }}>
          <div style={{ flex: '0 0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <h2 style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--ink-soft)' }}>
              {searching ? `Results for "${query}"` : 'Latest Articles'}
            </h2>
            <span style={{ fontSize: 11.5, color: 'var(--ink-soft)', opacity: 0.7 }}>
              {filtered.length} article{filtered.length === 1 ? '' : 's'}
            </span>
          </div>

          <div
            className="blog-cards-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, minmax(0,1fr))',
              gap: 28,
            }}
          >
            {loading && (
              <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--ink-soft)', fontSize: 14 }}>
                Loading articles…
              </div>
            )}
            {!loading && pageItems.map((post) => (
              <BlogCard key={post.slug} post={post} />
            ))}
            {!loading && !pageItems.length && (
              <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--ink-soft)', fontSize: 14 }}>
                {searching ? 'No articles match your search.' : 'No articles published yet — add one from the admin panel.'}
              </div>
            )}
          </div>

          {/* Pagination */}
          {!searching && totalPages > 1 && (
            <div className="blog-pagination" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', gap: 8, marginTop: 18 }}>
              <button
                onClick={() => goToPage(Math.max(1, safePage - 1))}
                disabled={safePage === 1}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '7px 16px', borderRadius: 999,
                  border: '1px solid var(--line)', background: 'white',
                  color: 'var(--ink)', fontSize: 12.5, fontWeight: 700,
                  opacity: safePage === 1 ? 0.4 : 1,
                  cursor: safePage === 1 ? 'default' : 'pointer',
                  marginRight: 4,
                }}
              >
                <span style={{ display: 'inline-flex', transform: 'rotate(180deg)' }}><ArrowIcon size={10} /></span> Prev
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
                <button
                  key={n}
                  onClick={() => goToPage(n)}
                  aria-label={`Go to page ${n}`}
                  aria-current={safePage === n ? 'page' : undefined}
                  style={{
                    width: 32, height: 32, borderRadius: '50%',
                    border: safePage === n ? 'none' : '1px solid var(--line)',
                    background: safePage === n ? 'var(--ink)' : 'white',
                    color: safePage === n ? 'white' : 'var(--ink)',
                    fontSize: 12.5, fontWeight: 700,
                  }}
                >
                  {n}
                </button>
              ))}
              <button
                onClick={() => goToPage(Math.min(totalPages, safePage + 1))}
                disabled={safePage === totalPages}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '7px 16px', borderRadius: 999,
                  border: '1px solid var(--line)', background: 'white',
                  color: 'var(--ink)', fontSize: 12.5, fontWeight: 700,
                  opacity: safePage === totalPages ? 0.4 : 1,
                  cursor: safePage === totalPages ? 'default' : 'pointer',
                  marginLeft: 4,
                }}
              >
                Next <ArrowIcon size={10} />
              </button>
            </div>
          )}
        </div>
      </div>

      <Footer hideCTA />
    </div>
  );
}
